// React and React Router

// Contexts

// Components
import { verifyLogin } from "util/cache"
import { useGetClassResultsQuery, useArchiveClassMutation, useRenameClassMutation } from "util/rtk-api"

import { DownloadActionDropdown, UpdateClassActionDropdown } from "components/action-dropdown"
import { DownloadClassButton, LessonButton, UpdateClassButton } from "components/blue-button"
import { AddNewStudentOverlay } from "components/classroom/add-new-student-overlay"
import Breadcrumb from "components/breadcrumb"
import { UpdateClassOverlay } from "components/classroom/update-class-overlay"
import { RenameClassOverlay } from "components/classroom/rename-class-overlay"
import { ConfirmationOverlay } from "components/confirmation-overlay"
import { DifficultyPills } from "components/tables/difficulty-pills"
import { StudentTable } from "components/tables/student-table"

// Utilities

import { useClassContext, ClassContextProvider } from "contexts/class-context"

// Styles
import styles from 'css/pages/classroom.module.css'
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useNavigate, useParams } from "react-router-dom"

// Types
import { Class } from '../types';

export const Classroom = () => {
    const { classId } = useParams()
    const { data: classDetails } = useGetClassResultsQuery({ classId: classId! }, { skip: !classId })
    const navigate = useNavigate()
    const [archiveClass, { isLoading: isArchiving }] = useArchiveClassMutation()
    const [renameClass] = useRenameClassMutation()
    const [downloadDropdownPos, setDownloadDropdownPos] = useState<{ top: number; left: number } | null>(null)
    const [currentDifficulty, setCurrentDifficulty] = useState('All')
    const [showUpdateClassDropdown, setShowUpdateClassDropdown] = useState(false)
    const [updateDropdownOpensUp, setUpdateDropdownOpensUp] = useState(false)
    const [showAddNewStudentOverlay, setShowAddNewStudentOverlay] = useState(false)
    const [showUpdateClassOverlay, setShowUpdateClassOverlay] = useState(false)
    const [showRenameClassOverlay, setShowRenameClassOverlay] = useState(false)
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
    const [isRenaming, setIsRenaming] = useState(false)
    const [renamingValue, setRenamingValue] = useState('')

    const downloadBtnRef = useRef<HTMLDivElement | null>(null)
    const downloadPortalRef = useRef<HTMLDivElement | null>(null)
    const updateContainerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        verifyLogin(navigate)
    }, [classDetails])

    const handleDownloadClick = () => {
        if (downloadDropdownPos) {
            setDownloadDropdownPos(null)
        } else if (downloadBtnRef.current) {
            const rect = downloadBtnRef.current.getBoundingClientRect()
            setDownloadDropdownPos({ top: rect.bottom + 4, left: rect.left })
        }
    }

    const handleUpdateClassClick = () => {
        if (!showUpdateClassDropdown && updateContainerRef.current) {
            const rect = updateContainerRef.current.getBoundingClientRect()
            setUpdateDropdownOpensUp(window.innerHeight - rect.bottom < 120)
        }
        setShowUpdateClassDropdown(!showUpdateClassDropdown)
    }

    const handleStartRename = () => {
        setRenamingValue(classDetails?.name || '')
        setIsRenaming(true)
    }

    const handleConfirmRename = async () => {
        if (renamingValue.trim() && classId && renamingValue.trim() !== classDetails?.name) {
            try {
                await renameClass({ classId, name: renamingValue.trim() })
            } catch (error) {
                console.error('Error renaming class:', error)
            }
        }
        setIsRenaming(false)
    }

    const handleCancelRename = () => {
        setIsRenaming(false)
        setRenamingValue('')
    }

    const handleRenameKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleConfirmRename()
        else if (e.key === 'Escape') handleCancelRename()
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node
            if (downloadDropdownPos &&
                downloadBtnRef.current && !downloadBtnRef.current.contains(target) &&
                downloadPortalRef.current && !downloadPortalRef.current.contains(target)) {
                setDownloadDropdownPos(null)
            }
            if (showUpdateClassDropdown && updateContainerRef.current && !updateContainerRef.current.contains(target)) {
                setShowUpdateClassDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [downloadDropdownPos, showUpdateClassDropdown])

    return (
        classDetails ? (
            <ClassContextProvider initialClass={classDetails} >
                <div className={styles.classroomContainer}>
                    <Breadcrumb items={[
                        { label: 'My Classrooms', path: '/classrooms' },
                        { label: classDetails?.name, path: `/classroom/${classDetails?.classId}` }
                    ]} />
                    {isRenaming ? (
                        <div className={styles.renameContainer}>
                            <input
                                type="text"
                                value={renamingValue}
                                onChange={(e) => setRenamingValue(e.target.value)}
                                onKeyDown={handleRenameKeyDown}
                                onBlur={handleConfirmRename}
                                className={styles.renameInput}
                                autoFocus
                            />
                        </div>
                    ) : (
                        <h2 className={styles.className}>
                            {classDetails?.name}
                            <button className={styles.renameLink} onClick={handleStartRename}>Rename Class</button>
                        </h2>
                    )}
                    Default Password: {classDetails?.defaultPassword}
                    <div className={styles.row}>
                        <DifficultyPills currentDifficulty={currentDifficulty} handleDifficultyChange={setCurrentDifficulty} />
                        <div className={`${styles.topRightButtonsContainer} `}>
                            <LessonButton onClick={() => navigate(`/lessons?classId=${classDetails?.classId}`)} />
                            <div className={styles.downloadContainer} ref={downloadBtnRef}>
                                <DownloadClassButton onClick={handleDownloadClick} />
                            </div>
                        </div>
                    </div>
                    <StudentTable
                        keyFilter={new Set(['difficulty', 'studentId', 'studentName', 'schoolStudentId', 'grade', 'lessonAverage'])}
                        style={{}}
                        clickableName={true}
                        renderHeader={true}
                        renderMoreActions={false}
                        currentDifficulty={currentDifficulty}
                    />
                </div>
                <div className={styles.alignRight} ref={updateContainerRef}>
                    <UpdateClassButton onClick={handleUpdateClassClick} />
                    {showUpdateClassDropdown && (
                        <UpdateClassActionDropdown
                            setShowAddNewStudentOverlay={setShowAddNewStudentOverlay}
                            setShowUpdateClassOverlay={setShowUpdateClassOverlay}
                            setShowRenameClassOverlay={setShowRenameClassOverlay}
                            setShowArchiveConfirm={setShowArchiveConfirm}
                            opensUp={updateDropdownOpensUp}
                        />
                    )}
                </div>
                {showAddNewStudentOverlay && (
                    <AddNewStudentOverlay setShowAddNewStudentOverlay={setShowAddNewStudentOverlay} className={classDetails?.name} />
                )}
                {showUpdateClassOverlay && (
                    <UpdateClassOverlay setShowUpdateClassOverlay={setShowUpdateClassOverlay} />
                )}
                {showRenameClassOverlay && (
                    <RenameClassOverlay
                        classId={classDetails?.classId}
                        currentName={classDetails?.name}
                        setShowRenameClassOverlay={setShowRenameClassOverlay}
                    />
                )}
                {showArchiveConfirm && (
                    <ConfirmationOverlay
                        title="Archive Class"
                        message={`Are you sure you want to archive "${classDetails?.name}"? The class and all its lessons will be hidden until unarchived.`}
                        confirmLabel="Archive"
                        onConfirm={async () => {
                            if (classId) {
                                await archiveClass({ classId })
                                navigate('/classrooms')
                            }
                        }}
                        onDismiss={() => setShowArchiveConfirm(false)}
                        isLoading={isArchiving}
                    />
                )}
                {downloadDropdownPos && createPortal(
                    <div ref={downloadPortalRef} style={{ position: 'fixed', top: downloadDropdownPos.top, left: downloadDropdownPos.left, zIndex: 9999 }}>
                        <DownloadActionDropdown />
                    </div>,
                    document.body
                )}
            </ClassContextProvider>
        ) : (
            <div>Loading...</div>
        )
    )
}